import { Text, type TextProps } from '@mantine/core';
import styles from './MoneyCell.module.css';

const formatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface MoneyCellProps extends Omit<TextProps, 'children'> {
  /** Числовое значение (string | number) */
  value: string | number | null | undefined;
  /** Суффикс валюты, например " ₽". По умолчанию не добавляется */
  suffix?: string;
  /** Заглушка при пустом значении (по умолчанию "—") */
  fallback?: string;
}

/**
 * Ячейка с форматированием денежной суммы (ru-RU, 2 знака).
 * Выравнивание по правому краю, дробная часть — приглушённым цветом.
 *
 * @example
 * cell: ({ row }) => <MoneyCell value={row.original.amount} suffix=" ₽" />
 */
const MoneyCell = ({ value, suffix, fallback = '—', fw = 400, ...textProps }: MoneyCellProps) => {
  if (value == null || value === '') {
    return (
      <Text fw={fw} ta='right' w='100%' pr={8} {...textProps}>
        {fallback}
      </Text>
    );
  }

  const num = Number(value);
  if (Number.isNaN(num)) {
    return (
      <Text fw={fw} ta='right' w='100%' pr={8} {...textProps}>
        {fallback}
      </Text>
    );
  }

  const formatted = formatter.format(num);
  const sepIndex = formatted.lastIndexOf(',');
  const intPart = sepIndex >= 0 ? formatted.slice(0, sepIndex) : formatted;
  const decPart = sepIndex >= 0 ? formatted.slice(sepIndex) : '';

  return (
    <Text fw={fw} ta='right' w='100%' pr={8} {...textProps}>
      {intPart}
      {decPart && <span className={styles.decimal}>{decPart}</span>}
      {suffix}
    </Text>
  );
};

export default MoneyCell;
